// Pure helpers for UpdateFieldModal's "adopt fresh Redux field" sync logic.
// Kept dependency-free (no React / webpack aliases) so they can be unit-tested
// in isolation with `node --test`.

// Deep equality by own-enumerable keys. Also used by the field save-diff logic
// in UpdateFieldModal (so it lives here as the single shared implementation).
const isEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return obj1 === obj2;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object')
    return obj1 === obj2;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let i = 0; i < keys1.length; i++) {
    const key = keys1[i];
    if (!keys2.includes(key) || !isEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

// A field minus its (continuously-updated) historical_data, used when comparing
// whether the *config* of a field changed (vs. just a history refresh).
const fieldConfigWithoutHistory = (field) => {
  if (!field) return field;
  const { historical_data, ...rest } = field;
  return rest;
};

// Decide whether an already-open Update modal should adopt fresh Redux field
// data (e.g. after a unit-system reload lands ~10s after the modal was opened)
// without discarding the user's in-progress edits.
//
//   isUpdateAction      - editing an existing field (not create/duplicate/activate)
//   hasOpenEditSection  - a sensor/time-frame edit section is open
//   freshField          - the latest Redux field
//   lastSyncedReduxField- the Redux field we last loaded from
//   currentField        - the modal's current (possibly edited) snapshot
//   lastSyncedSnapshot  - the snapshot we put into state at last sync
const shouldAdoptFreshField = ({
  isUpdateAction,
  hasOpenEditSection,
  freshField,
  lastSyncedReduxField,
  currentField,
  lastSyncedSnapshot,
}) => {
  if (!isUpdateAction) return false;
  if (hasOpenEditSection) return false;
  if (!freshField) return false;

  // Ignore history-only updates (they arrive continuously); compare config only.
  if (
    lastSyncedReduxField &&
    isEqual(
      fieldConfigWithoutHistory(freshField),
      fieldConfigWithoutHistory(lastSyncedReduxField),
    )
  ) {
    return false;
  }

  // The user has edited the form away from what we loaded — keep their input.
  if (lastSyncedSnapshot && !isEqual(currentField, lastSyncedSnapshot)) {
    return false;
  }

  return true;
};

module.exports = { isEqual, fieldConfigWithoutHistory, shouldAdoptFreshField };
